import {
  DatePickerField,
  FileUploader,
  Form,
  Loading,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { useUploadAttachments } from '@/features/attachment/_hooks/attachment';
import { useMe } from '@/features/authentication/_hooks/me';
import { useProject } from '@/features/project/_hooks/project';
import { TagSelectFieldForEdit } from '@/features/tag/components';
import { tagType } from '@/features/tag/models';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import { useMessage } from '@/providers';
import { isRS } from '@/utils';
import dayjs from 'dayjs';
import { type FC, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import { MAX_FILE_UPLOAD_LENGTH } from '@/features/workflow/constants';
import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import {
  useCreateWorkflow,
  useUpdateWorkflow,
  useWorkflow,
} from '@/features/workflow/hooks/workflow';
import type { WorkflowTarget } from '@/features/workflow/types/stack-sider-view';
import type {
  UpdateWorkflowParamsSchema,
  WorkflowFormSchema,
} from '../../../models/workflow';

type FormValues = z.infer<typeof WorkflowFormSchema>;
type InitialFormValues = Partial<FormValues>;

export const WORKFLOW_FORM_ID = 'workflow-form';

export const WorkflowForm: FC = () => {
  const { t } = useTranslation();
  const stackSiderView = useWorkflowStackSiderView();
  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'workflow-form', t('unreachable-code'));

  const { openMessage } = useMessage();
  const me = useMe();

  const isEdit = siderConfig.isEditing;

  const workflowId = isEdit ? siderConfig.id : '';
  const { data: workflow } = useWorkflow(workflowId);

  const peopleInCharge = usePeopleInCharges(siderConfig.target);

  const isDefaultSharedOrganizationIdsEnable =
    !isEdit && !!peopleInCharge.data?.results.length && !isRS();
  const defaultSharedOrganizationIds = useRecursiveOrganizationFilteredByKey({
    params: {
      ids:
        peopleInCharge.data?.results.flatMap((d) => d.organizationIds ?? []) ??
        [],
    },
    options: { separator: undefined, filterBy: 'id', reverse: true },
    enabled: isDefaultSharedOrganizationIdsEnable,
  });

  const isSavedSharedOrganizationIdsEnable =
    isEdit && !!workflow?.sharedOrganizations.length && !isRS();
  const savedSharedOrganizationIds = useRecursiveOrganizationFilteredByKey({
    params: {
      ids:
        workflow?.sharedOrganizations.map((organization) => organization.id) ??
        [],
    },
    options: { separator: undefined, filterBy: 'id', reverse: true },
    enabled: isSavedSharedOrganizationIdsEnable,
  });

  const { uploadAttachments } = useUploadAttachments();
  const { mutateAsync: createWorkflow } = useCreateWorkflow({
    projectId:
      siderConfig.target.key === 'project' ? siderConfig.target.id : undefined,
  });
  const { mutateAsync: updateWorkflow } = useUpdateWorkflow();

  const handleSubmit = async (values: FormValues, files: File[]) => {
    if (!me.data) {
      return;
    }

    let newWorkflowId = workflowId;
    const attachmentIds =
      values.attachments?.map((attachment) => attachment.id) ?? [];
    if (files.length) {
      const uploadedAttachmentIds = await uploadAttachments(files);
      if (uploadedAttachmentIds?.length) {
        attachmentIds.push(...uploadedAttachmentIds);
      }
    }

    const commonParams: Omit<
      z.infer<typeof UpdateWorkflowParamsSchema>,
      'id'
    > = {
      attachmentIds,
      assigneeIds: values.assigneeIds,
      content: values.content,
      deadline: values.deadline,
      remark: values.remark,
      tagIds: values.tagIds,
      // NOTE: sharedOrganizationIds is a nested array, so we need to extract the last organization id of each array
      sharedOrganizationIds: values.sharedOrganizationRecursiveIds.map(
        (organizationRecursiveIds) => organizationRecursiveIds.at(-1) ?? '',
      ),
      title: values.title,
    };
    if (isEdit) {
      await updateWorkflow({
        ...commonParams,
        id: workflowId,
      });
    } else {
      const res = await createWorkflow({
        ...commonParams,
        key: siderConfig.target.key,
        objectId: siderConfig.target.id,
      });
      newWorkflowId = res.id;
    }

    openMessage(
      'success',
      isEdit ? t('message-update-completed') : t('message-create-completed'),
    );

    if (isEdit) {
      stackSiderView.pop();
    } else {
      stackSiderView.set({
        payload: stackSiderView.list.toSpliced(-1, 1).concat({
          siderType: 'workflow',
          config: {
            viewType: 'workflow-details',
            id: newWorkflowId,
            target: siderConfig.target,
            stageViewType: 'list',
          },
        }),
        target: stackSiderView.target,
      });
    }
  };

  /**
   * NOTE: defaultSharedOrganizationIds/savedSharedOrganizationIdsが取得されるまで待たないと、
   * 初期値セット用のdefaultValuesのresetが初回しか行われないので、
   * defaultSharedOrganizationIds/savedSharedOrganizationIdsがundefinedでセットされてしまう。
   */
  if (
    (!isRS() &&
      ((!isEdit &&
        defaultSharedOrganizationIds.isPending &&
        isDefaultSharedOrganizationIdsEnable) ||
        (isEdit &&
          savedSharedOrganizationIds.isPending &&
          isSavedSharedOrganizationIdsEnable))) ||
    peopleInCharge.isPending
  ) {
    return <Loading />;
  }

  return (
    <WorkflowInnerForm
      onSubmit={handleSubmit}
      initialValues={
        workflow
          ? {
              ...workflow,
              deadline: workflow.deadline
                ? dayjs(workflow.deadline)
                : undefined,
              tagIds: workflow.tagTargets?.map((tag) => tag.tagId),
              sharedOrganizationRecursiveIds: isRS()
                ? []
                : savedSharedOrganizationIds.data,
            }
          : {
              assigneeIds: peopleInCharge.data?.results.map((d) => d.id) ?? [],
              sharedOrganizationRecursiveIds: isRS()
                ? []
                : defaultSharedOrganizationIds.data,
              ...(isRS() && {
                title: siderConfig.target.name,
              }),
            }
      }
      onFileChanged={(changed) => {
        stackSiderView.updateLastItem({
          config: {
            ...siderConfig,
            formValues: {
              uploadedFilesChanged: changed,
            },
          },
        });
      }}
    />
  );
};

const WorkflowInnerForm: FC<{
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues, file: File[]) => Promise<void>;
  onFileChanged: (changed: boolean) => void;
}> = ({ initialValues, onSubmit }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
    setValue,
  } = useFormContext<InitialFormValues, unknown, FormValues>();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { fields: attachmentFields, remove: removeAttachment } = useFieldArray({
    control,
    name: 'attachments',
  });

  useEffect(() => {
    if (!isDirty) {
      reset(
        (prev) => ({
          ...prev,
          ...initialValues,
        }),
        {
          keepDefaultValues: isRS(),
        },
      );
    }
  }, [isDirty, reset, initialValues]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      id={WORKFLOW_FORM_ID}
      layout="vertical"
      onFinish={handleSubmit(async (values) => {
        await onSubmit(values, uploadedFiles);
      })}
    >
      <TextInputField
        name="title"
        autocompleteKey="workflow.title"
        label={t('workflow.name')}
        control={control}
        errors={errors}
        required
      />
      <TagSelectFieldForEdit
        name="tagIds"
        control={control}
        errors={errors}
        type={tagType.Values.workflow}
        startYear="current"
        endYear="current"
      />
      <UserSelectField
        mode="multiple"
        name="assigneeIds"
        label={t('assignee')}
        control={control}
        errors={errors}
        useNotInOption
        required
        organizationFilter="modal"
      />
      <DatePickerField
        name="deadline"
        label={t('deadline')}
        picker="date"
        disabledDate={(current) => {
          return current < dayjs().subtract(1, 'day').endOf('day');
        }}
        shouldCoexistOnChange
        onChange={(value) => {
          if (!value) {
            setValue('deadline', undefined, { shouldDirty: true });
            return;
          }
          setValue('deadline', value, { shouldDirty: true });
        }}
        control={control}
        errors={errors}
      />
      {!isRS() && (
        <TextAreaInputField
          name="content"
          label={t('description')}
          control={control}
          errors={errors}
          rows={5}
          maxLength={1500}
        />
      )}
      <TextAreaInputField
        name="remark"
        label={t('remark')}
        control={control}
        errors={errors}
        rows={5}
        maxLength={300}
      />

      <Form.Item>
        <FileUploader
          files={uploadedFiles}
          maxFiles={MAX_FILE_UPLOAD_LENGTH}
          maxFileSelection={MAX_FILE_UPLOAD_LENGTH}
          disabled={uploadedFiles.length >= MAX_FILE_UPLOAD_LENGTH}
          onChange={(acceptedFiles) => {
            setUploadedFiles((prev) =>
              [...prev, ...acceptedFiles].slice(0, MAX_FILE_UPLOAD_LENGTH),
            );
          }}
          handleCancel={(index) => {
            const newFiles = [
              ...uploadedFiles.slice(0, index),
              ...uploadedFiles.slice(index + 1),
            ];
            setUploadedFiles(newFiles);
          }}
          storedFiles={{
            items: attachmentFields.map((field) => ({
              name: field.fileName,
              url: field.url,
              fileContentType: field.fileContentType,
            })),
            onCancel: removeAttachment,
          }}
          acceptableImage={!isRS()}
        />
      </Form.Item>
    </Form>
  );
};

const usePeopleInCharges = (target: WorkflowTarget) => {
  const { data: project } = useProject(
    target.key === 'project' ? target.id : '',
  );

  const peopleInCharge = useFilteredUsers({
    params: { filterBy: 'ids', ids: project?.peopleInChargeIds ?? [] },
    enabled: !!project?.peopleInChargeIds,
  });

  return peopleInCharge;
};
